import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  IconButton,
  Chip,
  Divider,
  Grid,
  Paper,
  Tooltip,
  InputAdornment,
  Snackbar
} from '@mui/material';
import {
  ContentCopy,
  Refresh,
  Visibility,
  VisibilityOff,
  Code,
  Send,
  CheckCircle
} from '@mui/icons-material';
import { ingestionService, stationService } from '../services/api';
import SensorMetricsReference from './SensorMetricsReference';

interface StationIngestionInfoProps {
  stationUuid: string;
  apiKey?: string;
}

const StationIngestionInfo: React.FC<StationIngestionInfoProps> = ({ stationUuid, apiKey }) => {
  const [ingestionInfo, setIngestionInfo] = useState<any>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    if (apiKey) {
      fetchIngestionInfo();
    }
  }, [apiKey]);

  const fetchIngestionInfo = async () => {
    if (!apiKey) return;

    try {
      const info = await ingestionService.getInfo(apiKey);
      setIngestionInfo(info);
    } catch (error) {
      console.error('Error fetching ingestion info:', error);
    }
  };

  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: `${label} copied to clipboard!` });
  };

  const handleRegenerateApiKey = async () => {
    if (!confirm('Are you sure you want to regenerate the API key? The old key will no longer work.')) {
      return;
    }

    setLoading(true);
    try {
      const result = await stationService.regenerateApiKey(stationUuid);
      setIngestionInfo({ ...ingestionInfo, api_key: result.api_key });
      setSnackbar({ open: true, message: 'API key regenerated successfully!' });
      // Refresh the parent component or notify it somehow
    } catch (error) {
      console.error('Error regenerating API key:', error);
      setSnackbar({ open: true, message: 'Failed to regenerate API key' });
    } finally {
      setLoading(false);
    }
  };

  const getEndpointUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/api/v1/ingest/${apiKey || '<api_key>'}/`;
  };

  const getCurlExample = () => {
    const endpoint = getEndpointUrl();
    const payload = JSON.stringify({
      readings: {
        temperature: 25.5,
        humidity: 60.0,
        pressure: 1013.25
      }
    }, null, 2);

    return `curl -X POST "${endpoint}" \\
  -H "Content-Type: application/json" \\
  -H "X-Timestamp: $(date -u +"%Y-%m-%dT%H:%M:%SZ")" \\
  -d '${payload}'`;
  };

  const getPythonExample = () => {
    const endpoint = getEndpointUrl();
    return `import requests
from datetime import datetime

endpoint = "${endpoint}"
headers = {
    "Content-Type": "application/json",
    "X-Timestamp": datetime.utcnow().isoformat() + "Z"
}

payload = {
    "readings": {
        "temperature": 25.5,
        "humidity": 60.0,
        "pressure": 1013.25,
        "wind_speed": 5.2,
        "wind_direction": 180
    }
}

response = requests.post(endpoint, json=payload, headers=headers)
print(f"Status: {response.status_code}")
print(f"Response: {response.json()}")`;
  };

  const getArduinoExample = () => {
    const endpoint = getEndpointUrl();
    return `#include <WiFi.h>
#include <HTTPClient.h>
#include <ArduinoJson.h>

const char* endpoint = "${endpoint}";

void sendSensorData(float temperature, float humidity) {
  if (WiFi.status() == WL_CONNECTED) {
    HTTPClient http;
    http.begin(endpoint);
    http.addHeader("Content-Type", "application/json");

    StaticJsonDocument<200> doc;
    JsonObject readings = doc.createNestedObject("readings");
    readings["temperature"] = temperature;
    readings["humidity"] = humidity;

    String payload;
    serializeJson(doc, payload);

    int httpCode = http.POST(payload);
    String response = http.getString();

    Serial.println("Status: " + String(httpCode));
    Serial.println("Response: " + response);

    http.end();
  }
}`;
  };

  if (!apiKey || !ingestionInfo) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Loading ingestion information...
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* API Key Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Code color="primary" />
            API Credentials
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="API Key"
                type={showApiKey ? 'text' : 'password'}
                value={ingestionInfo.api_key}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showApiKey ? 'Hide' : 'Show'}>
                        <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                          {showApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Copy API Key">
                        <IconButton
                          onClick={() => handleCopyToClipboard(ingestionInfo.api_key, 'API Key')}
                          edge="end"
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Regenerate API Key">
                        <IconButton
                          onClick={handleRegenerateApiKey}
                          disabled={loading}
                          edge="end"
                          color="warning"
                        >
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="HTTP Ingestion Endpoint"
                type={showApiKey ? 'text' : 'password'}
                value={getEndpointUrl()}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy Endpoint URL">
                        <IconButton
                          onClick={() => handleCopyToClipboard(getEndpointUrl(), 'Endpoint URL')}
                          edge="end"
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Keep your API key secure!</strong> Do not share it publicly or commit it to version control.
            </Typography>
          </Alert>
          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>About API Key Regeneration:</strong> The API key identifies your station. If you regenerate it:
              <br/>• The old key will stop working immediately
              <br/>• You must update your sensor/device with the new key
              <br/>• Historical measurements are NOT affected - they remain in the database
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* MQTT Endpoint (if using MQTT) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Code color="primary" />
            MQTT Configuration (Alternative to HTTP)
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="MQTT Broker"
                value={window.location.hostname}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy Broker Address">
                        <IconButton
                          onClick={() => handleCopyToClipboard(window.location.hostname, 'MQTT Broker')}
                          edge="end"
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="MQTT Port"
                value="1883"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="MQTT Topic"
                value={`sensors/${stationUuid}`}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy Topic">
                        <IconButton
                          onClick={() => handleCopyToClipboard(`sensors/${stationUuid}`, 'MQTT Topic')}
                          edge="end"
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>How MQTT Works:</strong>
              <br />• <strong>Topic</strong> (above): Where to publish the message - identifies which station
              <br />• <strong>station_name</strong> (in JSON): Your station UUID - must match the topic for validation
              <br />• <strong>Message</strong>: JSON with timestamp and measurements
            </Typography>
            <Typography variant="body2" component="div">
              <strong>Example Message:</strong>
              <br />
              <code style={{ display: 'block', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginTop: '8px', color: '#1e1e1e', fontFamily: 'Consolas, Monaco, "Courier New", monospace' }}>
                {`{\n  "station_name": "${stationUuid}",\n  "timestamp": "2025-10-21T12:00:00Z",\n  "temperature": 25.5,\n  "humidity": 60.0\n}`}
              </code>
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Comprehensive Metrics Reference */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <SensorMetricsReference protocol="both" />
        </CardContent>
      </Card>

      {/* JSON Example */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              Example Payload
            </Typography>
            <Tooltip title="Copy JSON">
              <IconButton
                onClick={() => handleCopyToClipboard(
                  JSON.stringify(ingestionInfo.example_payload, null, 2),
                  'Example JSON'
                )}
              >
                <ContentCopy />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1, overflow: 'auto' }}>
            <pre style={{
              margin: 0,
              color: '#d4d4d4',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: '13px'
            }}>
              {JSON.stringify(ingestionInfo.example_payload, null, 2)}
            </pre>
          </Paper>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Optional Header:</strong> X-Timestamp (ISO 8601 format or Unix timestamp)
              <br />
              If not provided, the server will use the current time.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Code Examples */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Code Examples
          </Typography>

          {/* cURL Example */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                cURL (Terminal)
              </Typography>
              <Tooltip title="Copy cURL command">
                <IconButton onClick={() => handleCopyToClipboard(getCurlExample(), 'cURL command')}>
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Box>
            <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1, overflow: 'auto' }}>
              <pre style={{
                margin: 0,
                color: '#d4d4d4',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '12px'
              }}>
                {getCurlExample()}
              </pre>
            </Paper>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Python Example */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Python
              </Typography>
              <Tooltip title="Copy Python code">
                <IconButton onClick={() => handleCopyToClipboard(getPythonExample(), 'Python code')}>
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Box>
            <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1, overflow: 'auto' }}>
              <pre style={{
                margin: 0,
                color: '#d4d4d4',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '12px'
              }}>
                {getPythonExample()}
              </pre>
            </Paper>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Arduino Example */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Arduino (ESP32/ESP8266)
              </Typography>
              <Tooltip title="Copy Arduino code">
                <IconButton onClick={() => handleCopyToClipboard(getArduinoExample(), 'Arduino code')}>
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Box>
            <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1, overflow: 'auto' }}>
              <pre style={{
                margin: 0,
                color: '#d4d4d4',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '12px'
              }}>
                {getArduinoExample()}
              </pre>
            </Paper>
          </Box>
        </CardContent>
      </Card>

      {/* Success Response */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            Expected Response
          </Typography>
          <Paper sx={{ bgcolor: '#1e1e1e', p: 2, borderRadius: 1 }}>
            <pre style={{
              margin: 0,
              color: '#4ec9b0',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: '13px'
            }}>
              {JSON.stringify({
                status: "success",
                message: "Data ingested successfully"
              }, null, 2)}
            </pre>
          </Paper>
        </CardContent>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default StationIngestionInfo;
